import { PageBar, Content } from '../components/Layout'
import { Panel } from '../components/ui'

interface Project {
  title: string
  tag: string
  difficulty: 'Starter' | 'Intermediate' | 'Hard'
  pitch: string
  detail: string[]
  caveat?: string
}

const PROJECTS: Project[] = [
  {
    title: 'Room conditions monitor',
    tag: 'Hardware',
    difficulty: 'Starter',
    pitch:
      'Temperature and humidity in the live room and the machine room, charted, with an alert when they drift.',
    detail: [
      'A Grade II listed building full of vintage Neumanns, valve outboard and a wooden live room.',
      'Humidity is what protects six figures of irreplaceable gear — this is the hardware idea with an actual business case, not a gimmick.',
      'Cheap sensor on a Pi or ESP32 → HTTP POST to our API → a chart and a threshold alert.',
      'Good first project: real wiring, real data, and nothing breaks if it fails.',
    ],
  },
  {
    title: 'Console control over MCP',
    tag: 'Hardware · AI',
    difficulty: 'Hard',
    pitch:
      'Expose the SSL Matrix 2 to an AI assistant as a set of tools, so you can ask it what the console is doing — and eventually, recall a session’s setup.',
    detail: [
      'MCP (Model Context Protocol) is how an AI assistant is given tools. You write a small server that declares what it can do; the assistant calls those tools.',
      'The Matrix 2 speaks Mackie Control (MCU), HUI and MIDI CC over Ethernet MIDI, and has 16 motorised faders — so software can both read positions and physically move them.',
      'Chain: Claude → MCP server (Python) → mido / python-rtmidi → ipMIDI → console.',
      'Tools worth exposing: get_console_state, set_fader, save_scene(booking_id), recall_scene.',
      'The payoff is save_scene tied to a booking in this app: “set the desk back to how it was for the Catfight session”. Every studio wants recall; most do it with a phone camera.',
    ],
    caveat:
      'MCU and HUI are DAW-control protocols. They reach the motorised faders and the DAW layer — they do NOT control the analogue signal path, so mic pre and insert routing still has to be documented another way. Start read-only, and never let it move anything during a live session.',
  },
  {
    title: 'Enquiry classifier, properly labelled',
    tag: 'AI',
    difficulty: 'Starter',
    pitch:
      'The triage model is trained on labels generated by keyword rules. Replace them with real human judgement and measure the honest accuracy.',
    detail: [
      'We have 453 real enquiries spanning five years. Nobody has read and labelled them by hand.',
      'The current 88% is measured against regex labels — it partly measures “can it learn my rules”, not truth.',
      'Deliberately oversample anything from a well-paid hobbyist: the model cannot recognise that segment today because we have never received one.',
      'Teaches the most under-taught thing in ML: your evaluation is only as honest as your labels.',
    ],
  },
  {
    title: 'Session provenance',
    tag: 'Blockchain',
    difficulty: 'Intermediate',
    pitch:
      'Hash a finished master plus its session metadata and anchor that hash publicly, so a client can prove what was made, when, and by whom.',
    detail: [
      'Anchor the hash, never the audio. Costs pennies per session on an L2 and the files never leave our storage.',
      'Pairs with session logging — the data is already being captured.',
      'What it actually buys: verifiability by a third party who does not trust us. Be precise about that, because it is the only thing it buys.',
    ],
    caveat:
      'UK copyright exists automatically on creation, and a notarised timestamp achieves nearly the same evidential weight with far less complexity. This is an exploration, not a committed part of the product — see ROADMAP.md v4.',
  },
  {
    title: 'Reply quality evaluation',
    tag: 'AI',
    difficulty: 'Intermediate',
    pitch:
      'Build a proper eval set for the drafted replies, so “the AI writes good emails” becomes a number instead of a feeling.',
    detail: [
      'Collect real enquiries, write the reply a good engineer would send, then score the model against them.',
      'Grade on specifics: does it quote the right rate, does it avoid promising a date, does it sound like a person.',
      'Without this, nobody can tell whether a prompt change helped or hurt.',
    ],
  },
]

const TONE: Record<Project['difficulty'], string> = {
  Starter: 'auto-live',
  Intermediate: 'auto-next',
  Hard: 'auto-later',
}

export function Lab() {
  return (
    <>
      <PageBar title="Lab" />
      <Content>
        <Panel pad>
          <p className="lede">
            Projects for people learning programming and AI on real equipment, with real data.
            Everything here is <b>deliberately off the booking path</b> — nothing in this list can
            stop a session being booked or an invoice going out if it breaks. That is the point:
            somewhere to try things without the studio paying for a mistake.
          </p>
          <p className="hint" style={{ marginTop: 10 }}>
            Pick one, read the detail, and ask before you start. Half of these have a catch worth
            knowing about first.
          </p>
        </Panel>

        <div className="lablist">
          {PROJECTS.map((p) => (
            <Panel key={p.title} pad className="labcard">
              <div className="autohead">
                <b>{p.title}</b>
                <span className="labtags">
                  <span className="badge b-test">{p.tag}</span>
                  <span className={`badge ${TONE[p.difficulty]}`}>{p.difficulty}</span>
                </span>
              </div>
              <p className="autowhat">{p.pitch}</p>
              <ul className="labdetail">
                {p.detail.map((line) => <li key={line}>{line}</li>)}
              </ul>
              {p.caveat && (
                <p className="labcaveat"><span>Catch</span> {p.caveat}</p>
              )}
            </Panel>
          ))}
        </div>
      </Content>
    </>
  )
}
